import { useRef, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Camera,
  Upload,
  Link as LinkIcon,
  Loader2,
  Trash2,
  ImageIcon,
} from "lucide-react";
import { resizeImage, isMobileLikely } from "@/lib/imageResize";
import { UploadsAPI, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * VehiclePhotoPicker — drop-in replacement for the old "Image URL" input.
 *
 * Exposes three input modes:
 *   • Upload — file picker (accept="image/*")
 *   • Take photo — opens the device camera on mobile via capture="environment"
 *   • Paste URL — keeps the legacy direct-link affordance the user asked us to retain
 *
 * Photos are resized client-side to ≤1600px JPEG q=0.85 before upload, sent to
 * /api/uploads/vehicle-photo, and the returned URL is hoisted into `value`
 * via the onChange callback. The component itself stays controlled — parent
 * owns `value` (the resolved image_url string).
 *
 * `testIdPrefix` lets the picker live in multiple dialogs side-by-side
 * (wizard vs. detail sheet) with stable test ids.
 */
export default function VehiclePhotoPicker({
  value,
  onChange,
  testIdPrefix = "photo-picker",
}) {
  const [mode, setMode] = useState("upload"); // "upload" | "url"
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const showCamera = isMobileLikely();

  const previewSrc = resolveImageUrl(value);

  const [previewBroken, setPreviewBroken] = useState(false);
  useEffect(() => {
    setPreviewBroken(false);
  }, [value]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { blob } = await resizeImage(file);
      const filename = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
      const res = await UploadsAPI.uploadVehiclePhoto(blob, filename);
      onChange?.(res.url);
      toast.success("Photo uploaded");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
      // Clear the input value so picking the same file twice still fires onChange.
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const handleClear = () => onChange?.("");

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-root`}>
      {/* Preview pane */}
      <div
        className="relative w-full aspect-[16/9] bg-[var(--bg-canvas)] border border-[var(--border-hover)] overflow-hidden flex items-center justify-center"
        style={{ borderRadius: 4 }}
        data-testid={`${testIdPrefix}-preview`}
      >
        {previewSrc && !previewBroken ? (
          <>
            <img
              src={previewSrc}
              alt="Vehicle"
              className="w-full h-full object-cover"
              onError={() => setPreviewBroken(true)}
            />
            <button
              type="button"
              onClick={handleClear}
              className="absolute top-2 right-2 w-7 h-7 bg-black/70 hover:bg-black border border-[#3a1212] text-[#E82127] flex items-center justify-center transition-colors"
              style={{ borderRadius: 2 }}
              aria-label="Remove photo"
              data-testid={`${testIdPrefix}-clear-btn`}
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-[#5a5a5e]">
            <ImageIcon className="w-7 h-7" strokeWidth={1.25} />
            <span className="text-[11px] tracking-wider uppercase">
              {previewBroken ? "Image failed to load" : "No photo yet"}
            </span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2 text-[var(--text-primary)] text-xs" data-testid={`${testIdPrefix}-uploading`}>
            <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.75} />
            <span className="tracking-wider uppercase text-[10px]">Resizing &amp; uploading…</span>
          </div>
        )}
      </div>

      {/* Action row — mode-aware */}
      {mode === "upload" ? (
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            variant="outline"
            className="bg-[var(--bg-input)] border-[var(--border-subtle)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)] h-9 text-xs"
            data-testid={`${testIdPrefix}-upload-btn`}
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />
            Upload
          </Button>
          {showCamera ? (
            <Button
              type="button"
              onClick={() => cameraRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="bg-[var(--bg-input)] border-[var(--border-subtle)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)] h-9 text-xs"
              data-testid={`${testIdPrefix}-camera-btn`}
            >
              <Camera className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />
              Take photo
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => setMode("url")}
              variant="outline"
              className="bg-[var(--bg-input)] border-[var(--border-subtle)] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] h-9 text-xs"
              data-testid={`${testIdPrefix}-url-mode-btn`}
            >
              <LinkIcon className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />
              Paste URL
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-1.5">
          <Input
            value={value || ""}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder="https://example.com/photo.jpg"
            className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] text-xs"
            data-testid={`${testIdPrefix}-url-input`}
          />
          <button
            type="button"
            onClick={() => setMode("upload")}
            className="text-[11px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] tracking-wider"
            data-testid={`${testIdPrefix}-upload-mode-btn`}
          >
            ← Back to upload
          </button>
        </div>
      )}

      {/* On mobile, surface the URL toggle separately since the camera button took its slot. */}
      {mode === "upload" && showCamera && (
        <button
          type="button"
          onClick={() => setMode("url")}
          className="text-[11px] text-[#5a5a5e] hover:text-[var(--text-secondary)] tracking-wider transition-colors"
          data-testid={`${testIdPrefix}-url-mode-secondary-btn`}
        >
          Or paste a URL instead
        </button>
      )}

      {/* Hidden inputs — file picker + camera capture. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testIdPrefix}-file-input`}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testIdPrefix}-camera-input`}
      />
    </div>
  );
}
